import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import * as Sentry from '@sentry/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/modules/shared/components/button/Button'

interface SentryFeedbackDialogProps {
  isOpen: boolean
  onClose: () => void
}

export function SentryFeedbackDialog({ isOpen, onClose }: SentryFeedbackDialogProps) {
  const { t } = useTranslation()
  const [feedbackMessage, setFeedbackMessage] = useState('')
  const [feedbackEmail, setFeedbackEmail] = useState('')
  const [feedbackName, setFeedbackName] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [feedbackSent, setFeedbackSent] = useState(false)

  const submitFeedback = () => {
    if (!feedbackMessage) return

    setIsSending(true)

    try {
      Sentry.captureFeedback({
        name: feedbackName,
        email: feedbackEmail,
        message: feedbackMessage,
      })

      window.setTimeout(() => {
        setIsSending(false)
        setFeedbackSent(true)

        window.setTimeout(() => {
          handleClose()

          window.setTimeout(() => {
            setFeedbackSent(false)
          }, 300)
        }, 1500)
      }, 500)
    } catch (error) {
      console.error('Error submitting feedback:', error)
      setIsSending(false)
    }
  }

  const handleClose = () => {
    if (isSending) return

    setFeedbackName('')
    setFeedbackEmail('')
    setFeedbackMessage('')
    onClose()
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
          <DialogTitle className="text-lg font-medium text-gray-900">
            {feedbackSent
              ? t('general.reportBugSuccess', 'Feedback Submitted')
              : t('general.reportABug', 'Report a Bug')}
          </DialogTitle>

          {!feedbackSent ? (
            <>
              <div className="mt-4 space-y-4">
                <div>
                  <label htmlFor="feedbackName" className="block text-sm font-medium text-gray-700">
                    {t('general.name', 'Name')}
                  </label>
                  <input
                    type="text"
                    id="feedbackName"
                    value={feedbackName}
                    onChange={(e) => setFeedbackName(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="feedbackEmail" className="block text-sm font-medium text-gray-700">
                    {t('general.email', 'Email')}
                  </label>
                  <input
                    type="email"
                    id="feedbackEmail"
                    value={feedbackEmail}
                    onChange={(e) => setFeedbackEmail(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="feedbackMessage" className="block text-sm font-medium text-gray-700">
                    {t('general.description', 'Description')} *
                  </label>
                  <textarea
                    id="feedbackMessage"
                    rows={4}
                    required
                    value={feedbackMessage}
                    onChange={(e) => setFeedbackMessage(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none sm:text-sm"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <Button
                  className="border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                  onClick={handleClose}
                  disabled={isSending}
                >
                  {t('general.cancel', 'Cancel')}
                </Button>
                <Button
                  className="border border-primary/80 bg-primary px-4 py-2 text-sm font-medium text-white shadow-md hover:bg-primary/80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  onClick={submitFeedback}
                  loading={isSending}
                  disabled={!feedbackMessage || isSending}
                >
                  {t('general.submit', 'Submit')}
                </Button>
              </div>
            </>
          ) : (
            <div className="mt-4 text-center">
              <p>{t('sentryFeedback.thankYouForFeedback', 'Thank you for your feedback!')}</p>
            </div>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  )
}
